import { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  downloadPdfUtility,
  handlePrintUtility,
  API_STATUS_SUCCESS,
  API_STATUS_CONFLICT,
  toSentancePascalCase,
  useAnalytics
} from 'common';
import {
  useModMemberIdCard,
  useMemberIDCardRequest,
} from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';
import {
  Button,
  ModalOverlay,
  Headings,
  GlobalError,
  Loader,
  Paragraph,
  Link,
  PrintIcon,
  DownloadArrowIcon,
  FaxIcon,
  RequestIdCardIcon
} from '@sentara-health/optimahealth_web';
import MemberSelectDropdown from '../medicalClaims/MemberSelectDropdown';

// Initialize a temporary array to store checkbox selections
let _tempArr: any[] = [];

const MemberIdCardTable = ({
  mapState,
  setIsOpenFax,
  OrderMemberIDCard,
  faxIDCard,
}: any) => {
  //  Initialize useTranslation hook
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // To manage the loader
  const [loader, setLoader] = useState(false);
  // To manages the error messages
  const [errorMessage, setErrorMessage] = useState(false);
  const [requestIdCardError, setRequestIdCardError] = useState(false);
  const [showBtn, setShowBtn] = useState<any>([]);
  // To manage PDF functionality
  const { fetchMemberIdCard } = useModMemberIdCard();
  const { primaryUser, loginMID, serviceNumber, setSelectedIdCardMemberId } = useContext(GlobalContext);
  const [pdfData, setPdfData] = useState<string | null>(''); 
   
  const { memberIDCardRequestType } = useMemberIDCardRequest();
  const [formData, setFormData] = useState<any>();
  const [frontImgData, setFrontImgData] = useState<string | null>('');
  const [backImgData, setBackImgData] = useState<string | null>('');
  const [showBackIdCard, setShowBackIdCard] = useState<boolean>(false);
  const [selectedMID, setSelectedMID] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [selectedMemberName, setSelectedMemberName] = useState<string>('');
  const [phone, setPhone] = useState<any>();
  const hasTrackedView = useRef(false);
  const { trackContentListing } = useAnalytics();

  const getSelectedMember = () =>
    mapState?.find((member: any) => member.mId === selectedMID);

  const trackMemberIdCardAdobeEvent = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    const selectedMember = getSelectedMember();
    trackContentListing(
      contentId,
      contentTitle,
      itemPosition,
      undefined,
      {
        extraFields: {
          ...(selectedMember?.cardType
            ? { cardType: selectedMember.cardType }
            : {}),
          ...(selectedMember?.planType
            ? { planType: selectedMember.planType }
            : {}),
          interactionSource: 'Member ID Card Details Page',
        },
      }
    );
  };

  // Function to request member ID card
  const memberShowIdCard = async (e: any): Promise<void> => {
    e.preventDefault();
    setLoader(true);
    if (showBtn.length > 0) {
      setShowBtn(_tempArr);
    }

     const postData = {
      "identifier": [{
        "system": "memberId",
        "value": selectedMemberId
      }]
    }
    const response = await memberIDCardRequestType(postData);
    let heading = '';
    let body = '';
    let button = '';
    if (response && response.errorCode === API_STATUS_CONFLICT) {
      heading = t('memberIdCard.memberRequestIdErrorHeading');
      body = 'ERR_MSG';
      button = 'Cancel';
    } else if(response && response?.data?.status === API_STATUS_SUCCESS) {
      heading = t('memberIdCard.memberRequestIdSuccessHeading');
      body = 'SUCCESS_MSG';
      button = 'OK';
    } else {
      setLoader(false);
      setRequestIdCardError(true);
      return;
    }
    setFormData({ heading, body, button });
    setLoader(false);
    if (response.data) {
      setShowBtn(_tempArr);
    }
    setIsOpen(true);
    setErrorMessage(false);
  };

  useEffect(() => {
    setPhone(serviceNumber ? JSON.parse(serviceNumber) : {});
  }, [serviceNumber]);
  
  const handlePrintDownload = async (actionType: 'print' | 'download') => {
    setLoader(true);
    let pdfContent = pdfData;
    if (!pdfContent) {
      const response = await fetchMemberIdCard({ memberId: selectedMemberId, formatType: 'pdf' });
      pdfContent = response?.data?.files?.[0]?.image ?? null;
      setPdfData(pdfContent);
    }
    if (pdfContent) {
      if (actionType === 'print') {
        trackMemberIdCardAdobeEvent('302', 'Member_ID_card_print_click', 2);
        handlePrintUtility(pdfContent);
      } else if (actionType === 'download') {
        trackMemberIdCardAdobeEvent('301', 'Member_ID_card_download_click', 1);
        downloadPdfUtility(pdfContent, 'MemberList.pdf', 'application/pdf');
      }
    }
    setLoader(false);
  };

  
  //Function to show PDF card
  const pdfShowCard = useCallback(
  async (Id: string) => {
    setErrorMessage(false);
    setRequestIdCardError(false);
    setLoader(true);
    try {
      const response = await fetchMemberIdCard({ memberId: Id, formatType: 'sidesAsPng' });
      if (response?.error) {
        setErrorMessage(true);
      } else {
        const files = response?.data?.files?.[0] ?? {};
        const frontImage = files.front ?? null;
        const backImage = files.back ?? null;
        setFrontImgData(frontImage);
        setBackImgData(backImage);
        if (!frontImage && !backImage) {
          setErrorMessage(true);
        } else if (!hasTrackedView.current) {
          hasTrackedView.current = true;
          trackMemberIdCardAdobeEvent('300', 'Member_ID_card_view_click', 0);
        }
      }
    } catch {
      setErrorMessage(true);
    } finally {
      setLoader(false);
    }
  },
  [fetchMemberIdCard]
);  


  const toggleBackFrontIdCard = () => {
    setShowBackIdCard(!showBackIdCard);
  };

  // Initialize selectedMID only once when mapState is first loaded
  useEffect(() => {
    if (mapState?.length > 0 && !selectedMID) {
      setSelectedMID(mapState[0].mId);
      setSelectedIdCardMemberId(mapState[0].memberId);
    }
  }, [mapState, selectedMID, setSelectedIdCardMemberId]);

  // Load ID card images when selectedMID changes and mapState is available
  useEffect(() => {
    if (selectedMID && mapState?.length > 0) {
      const selectedMember = mapState.find((m: any) => m.mId === selectedMID);
      if (selectedMember) {
        if (selectedMemberId !== selectedMember.memberId) {
          setSelectedMemberId(selectedMember.memberId);
          setSelectedMemberName(selectedMember.memberName);
          pdfShowCard(selectedMember.memberId);
          setSelectedIdCardMemberId(selectedMember.memberId);
          setShowBackIdCard(false); // Reset to Id card front view when member changes
          setPdfData(null); // Clear previous PDF data when member changes
        }
      }
    }
  }, [selectedMID, mapState, selectedMemberId, fetchMemberIdCard, pdfShowCard, setSelectedIdCardMemberId]); 

  return (
    <>
      {loader && <Loader />}     
      <div>
      <div className='row'>
        <div className="col-md-6 col-12 mb-3">
                    <MemberSelectDropdown
                      members={mapState}
                      memberId={selectedMID}
                      setMemberId={setSelectedMID}
                      primaryUser={primaryUser}
                      loginMID={loginMID}
                      disableWhenSingleMember={true}
                      isMemberIdCardDropdown={true}
                    />
                </div>
                <div className="col-md-6 col-12 d-none d-md-flex flex-row gap-4 mb-3 justify-content-end">
                  {!errorMessage && OrderMemberIDCard  ? (
                  <Button
                    iconLeft={<RequestIdCardIcon fill="black" variant="icon" />}
                    data-testid="requestid_card_button"
                    size="s"
                    color="black"
                    variant="icon_btn"
                    onClick={(e: any) => memberShowIdCard(e)}
                  >
                     {t('memberIdCard.requestIDCard')}
                  </Button>
                  ) : null}
                  {!errorMessage && faxIDCard ? (
                    <Button
                      iconLeft={<FaxIcon fill="black" variant="icon" />}
                      data-testid="fax_button"
                      size="s"
                      color="black"
                      variant="icon_btn"
                      onClick={() => {
                        setIsOpenFax();
                        setErrorMessage(false);
                        setRequestIdCardError(false);
                      }}
                    >
                      {t('memberIdCard.fax')}
                    </Button>
                  ) : null}
                </div>
        </div>            
        {errorMessage && (
          <GlobalError variant="mb-3" dataTestId="memberIdCardError">
            {t('yourPlan.memberIdCardError')}
          </GlobalError>
        )}
        {requestIdCardError && (
          <GlobalError variant="mb-3" dataTestId="memberIdCardError">
            {t('common.internalServerError')}
          </GlobalError>
        )}
        {!errorMessage && frontImgData && backImgData && (
          <>
            <div className="row mb-1 mb-md-3">
              <div className="col-12 col-md-6 mb-3">
                <div className="border rounded-3 p-1">
                  <img src={`data:image/png;base64,${showBackIdCard ? backImgData : frontImgData}`} alt="ID Card" className='img-fluid' />
                </div>
              </div>
              <div className="col-md-6 d-none d-md-block">
                <div className="border rounded-3 p-1">
                  <img src={`data:image/png;base64,${backImgData}`} alt="ID Card Back" className='img-fluid' />
                </div>
              </div>
            </div>
            <div className="d-flex flex-lg-row flex-md-row flex-column gap-3 gap-lg-3 gap-md-3 mb-3">
              <Button
                data-testid="view_back_icard_button"
                onClick={toggleBackFrontIdCard}
                variant="secondary_btn"
                className="d-block d-md-none"
              >
                {showBackIdCard ? t('memberIdCard.viewFront') : t('memberIdCard.viewBack')}
              </Button>
              <Button
                iconLeft={<PrintIcon fill="white" variant="icon" />}
                data-testid="print_button"
                onClick={() => handlePrintDownload('print') }
                variant="primary_btn"
                className="d-none d-md-flex"
              >
                {t('common.print')}
              </Button>
              <Button
                iconLeft={<DownloadArrowIcon color="white" variant="icon" />}
                data-testid="download_button"
                onClick={() => handlePrintDownload('download') }
                variant="primary_btn"
                className="d-none d-md-flex"
              >
                {t('common.download')}
              </Button>
              <Button
                data-testid="download_icard_button"
                onClick={() => handlePrintDownload('download') }
                variant="primary_btn"
                className="d-block d-md-none"
              >
                {t('common.print')}/{t('common.download')}
              </Button>
            </div>
            <hr className="d-block d-md-none my-2" />
            <div className="d-flex flex-column flex-md-row gap-3 gap-md-3 mt-3">
              {OrderMemberIDCard ? (
                <Button
                  dataTestId="requestid_card_button"
                  onClick={(e: any) => memberShowIdCard(e)}
                  variant="secondary_btn"
                  className="d-md-none d-block w-auto"
                  disabled={mapState?.length < 1}
                >
                  {t('memberIdCard.requestIDCard')}
                </Button>
              ): null}
              {faxIDCard ? (
                <Button
                  dataTestId="fax_button"
                  variant="secondary_btn"
                  className="d-md-none d-block w-auto"
                  disabled={mapState?.length < 1}
                  onClick={() => {
                    setIsOpenFax();
                    setErrorMessage(false);
                    setRequestIdCardError(false);
                  }}
                >
                  {t('memberIdCard.fax')}
                </Button>
              ) : null}
            </div>
          </>
        )}
        {formData && (
          <ModalOverlay
            isOpen={isOpen}
            onClose={setIsOpen}
            overlayInfo="Modal information"
          >
            <Headings
              text={formData.heading}
              dataTestId="PleaseNoteTitle"
              variant="semiBold"
            />
            <div data-testid="requestIdNote">
                <Paragraph
                text={
                    <Trans
                    i18nKey={
                      formData.body === 'ERR_MSG'
                        ? 'memberIdCard.memberRequestIdMessage'
                        : 'memberIdCard.memberRequestIdSuccessMessage'
                        }
                        values={{
                            phoneNumber1: phone?.data?.benefitPhone1800,
                            name: toSentancePascalCase(selectedMemberName),
                        }}
                        components={{
                            phoneNumber1: (
                                <Link
                                    to={`tel:${phone?.data?.benefitPhone1800}`}
                                    className="text-decoration-underline"
                                />
                            ),                            
                        }}
                    />
                }
                variant="regular"
                data-testid="combinedDisclaimer"
                className="mb-5 lineBrake"
            />
                
              </div>
            <div className="d-flex mt-4 gap-3 flex-wrap">
              <Button
                iconLeft={<PrintIcon fill="white" variant="icon" />}
                dataTestId="request_print_button"
                onClick={() => handlePrintDownload('print')}
                variant="primary_btn"
                className="flex-fill"
              >
                {t('common.print')}
              </Button>
              <Button
                iconLeft={<DownloadArrowIcon color="white" variant="icon" />}
                dataTestId="request_download_button"
                onClick={() => handlePrintDownload('download')}
                variant="primary_btn"
                className="flex-fill"
              >
                {t('common.download')}
              </Button>
              <Button
                dataTestId="close_button"
                onClick={() => setIsOpen(false)}
                variant="secondary"
                className="flex-fill"
              >
                {t(formData.button)}
              </Button>
            </div>
          </ModalOverlay>
        )}
      </div>
    </>
  );
};

export default MemberIdCardTable;
